import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, FormControlLabel, IconButton, MenuItem, Snackbar, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import RedeemIcon from '@mui/icons-material/Redeem';
import RuleIcon from '@mui/icons-material/Rule';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import {
    createSubscriptionOfferRule, createSubscriptionOfferTemplate, deleteSubscriptionOfferRule, getSubscriptionOffers, getUserTags,
    updateSubscriptionOfferRule, updateSubscriptionOfferTemplate,
} from '../Api';
import {
    EffectOptionDto, SubscriptionOfferRuleDto, SubscriptionOfferRuleRequest, SubscriptionOffersAdminDto,
    SubscriptionOfferTemplateDto, SubscriptionOfferTemplateRequest, SubscriptionPlanAdminDto,
} from '../models/dtos/UserPremiumAdminDto';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';
import { benefitView, GROUP_INFO, groupIcon, OfferBoosts } from '../components/UserSubscriptionsCard';
import SubscriptionTierChip from '../components/SubscriptionTierChip';
import { ConfirmDialog } from '../components/betupFeed/BetupFeedShared';

type OffersResult = { data: SubscriptionOffersAdminDto | null; error: string | null };

const MAX_CLAIM_DAYS = 90;

function formatDate(value: string | number | null): string {
    return value == null ? '—' : new Date(value).toLocaleString();
}

function formatDay(value: string | number | null): string {
    return value == null ? '—' : new Date(value).toLocaleDateString();
}

/** `datetime-local` value in the browser's zone. */
function toLocalInput(value: string | number | null): string {
    if (value == null) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toIsoOrNull(value: string): string | null {
    return value.trim() ? new Date(value).toISOString() : null;
}

function percent(part: number, whole: number): string {
    return whole === 0 ? '—' : `${Math.round((part / whole) * 1000) / 10}%`;
}

/** A plan's own effect of `group` (its coef is what an offer boost must beat), or null. */
function planEffectOf(plan: SubscriptionPlanAdminDto, group: number, options: EffectOptionDto[]): EffectOptionDto | null {
    for (const b of plan.benefits) {
        if (b.type !== 'EFFECT') continue;
        const option = options.find(o => o.id === b.effectId);
        if (option?.group === group) return option;
    }
    return null;
}

function planDailyCoins(plan: SubscriptionPlanAdminDto): number {
    return plan.benefits.filter(b => b.type === 'DAILY_COINS').reduce((max, b) => Math.max(max, b.dailyCoins ?? 0), 0);
}

/**
 * Marketing › Subscriptions › Offers (analytics SubscriptionOfferAdminService, betup-server-api docs/subscriptions.md §15):
 * benefit offer templates — per active plan, stronger effects of its groups and / or more daily betcoins — and the rules
 * giving them to players by tags at a session start. A player takes an offer by subscribing before it expires; it then
 * lasts with the subscription (an upgrade carries it, a downgrade drops it). Give one by hand from a player's Premium tab.
 */
export default function SubscriptionOffersPage() {
    const navigate = useNavigate();
    const [data, setData] = React.useState<SubscriptionOffersAdminDto | null>(null);
    const [tags, setTags] = React.useState<UserTagDefinitionDto[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [toast, setToast] = React.useState<string | null>(null);
    const [editingTemplate, setEditingTemplate] = React.useState<SubscriptionOfferTemplateDto | 'new' | null>(null);
    const [editingRule, setEditingRule] = React.useState<SubscriptionOfferRuleDto | 'new' | null>(null);
    const [deletingRule, setDeletingRule] = React.useState<SubscriptionOfferRuleDto | null>(null);
    const [busy, setBusy] = React.useState(false);

    const load = React.useCallback(async () => {
        setLoading(true);
        const [offers, userTags] = await Promise.all([getSubscriptionOffers(), getUserTags()]);
        setLoading(false);
        setData(offers);
        setTags(userTags ?? []);
        setError(offers == null ? 'Failed to load the subscription offers.' : null);
    }, []);

    React.useEffect(() => { load(); }, [load]);

    /** Runs a write; on success takes the new state and toasts `done`. Resolves the error (null = done). */
    const run = async (action: () => Promise<OffersResult>, done: string): Promise<string | null> => {
        setBusy(true);
        const result = await action();
        setBusy(false);
        if (result.error != null || result.data == null) return result.error ?? 'Saving failed';
        setData(result.data);
        setToast(done);
        return null;
    };

    const toggleRule = async (rule: SubscriptionOfferRuleDto) => {
        const failed = await run(() => updateSubscriptionOfferRule(rule.id, { ...ruleRequestOf(rule), enabled: !rule.enabled }),
            `${rule.name} ${rule.enabled ? 'disabled' : 'enabled'}`);
        setError(failed);
    };

    const plans = data?.catalogue.plans ?? [];
    const options = data?.catalogue.effectOptions ?? [];

    return (
        <Box sx={{ mx: 3, my: 3 }}>
            <Stack spacing={3}>
                <Stack direction="row" gap={1.5} alignItems="center">
                    <IconButton onClick={() => navigate('/marketing/subscriptions')}><ArrowBackIcon /></IconButton>
                    <LocalOfferOutlinedIcon color="primary" fontSize="large" />
                    <Typography variant="h4">Subscription offers</Typography>
                    <Tooltip title={'A template boosts every active plan: stronger effects of its groups (one per group) and / or more daily '
                        + 'betcoins. Given to a player (by a rule at a session start, or from their Premium tab), it is taken by subscribing '
                        + 'before it expires and lasts with that subscription; an upgrade carries it, a downgrade drops it.'}>
                        <InfoOutlinedIcon color="action" />
                    </Tooltip>
                    <Box sx={{ flexGrow: 1 }} />
                    {(loading || busy) && <CircularProgress size={22} />}
                    <Button variant="outlined" onClick={load} disabled={loading}>Refresh</Button>
                </Stack>
                {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
                {loading && data == null && <CircularProgress />}

                {data && (
                    <>
                        {/* Templates */}
                        <Card variant="outlined">
                            <CardContent>
                                <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1.5 }}>
                                    <LocalOfferOutlinedIcon color="primary" />
                                    <Typography variant="h6">Templates</Typography>
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditingTemplate('new')}>New template</Button>
                                </Stack>
                                {data.templates.length === 0
                                    ? <Typography variant="body2" color="text.secondary">No templates yet.</Typography>
                                    : (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>State</TableCell>
                                                    <TableCell>Boosts per plan</TableCell>
                                                    <TableCell align="right">Claim days</TableCell>
                                                    <TableCell align="right">Given</TableCell>
                                                    <TableCell align="right">Accepted</TableCell>
                                                    <TableCell align="right">Open</TableCell>
                                                    <TableCell align="right">Acceptance</TableCell>
                                                    <TableCell>Updated</TableCell>
                                                    <TableCell align="right" />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {data.templates.map(t => (
                                                    <TableRow key={t.id} sx={{ opacity: t.active ? 1 : 0.6 }}>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={600}>{t.name}</Typography>
                                                            <Typography variant="caption" color="text.secondary">#{t.id}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {t.active ? <Chip size="small" color="success" label="active" /> : <Chip size="small" label="inactive" />}
                                                        </TableCell>
                                                        <TableCell sx={{ minWidth: 300 }}><OfferBoosts benefits={t.benefits} plans={plans} options={options} /></TableCell>
                                                        <TableCell align="right">{t.claimDays}</TableCell>
                                                        <TableCell align="right">{t.given.toLocaleString()}</TableCell>
                                                        <TableCell align="right">{t.accepted.toLocaleString()}</TableCell>
                                                        <TableCell align="right">{t.open.toLocaleString()}</TableCell>
                                                        <TableCell align="right">
                                                            <Tooltip title="Accepted / given">
                                                                <Typography variant="body2" fontWeight={600}>{percent(t.accepted, t.given)}</Typography>
                                                            </Tooltip>
                                                        </TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDay(t.updatedAt)}</TableCell>
                                                        <TableCell align="right">
                                                            <Tooltip title="Edit">
                                                                <IconButton size="small" onClick={() => setEditingTemplate(t)}><EditOutlinedIcon fontSize="small" /></IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                            </CardContent>
                        </Card>

                        {/* Rules */}
                        <Card variant="outlined">
                            <CardContent>
                                <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1.5 }}>
                                    <RuleIcon color="primary" />
                                    <Typography variant="h6">Rules</Typography>
                                    <Tooltip title={'At a session start, every enabled rule open now whose tags and OS fit the player gives its template '
                                        + 'through the Api (highest priority first), which refuses what isn\'t for them. Each rule is tried once per player: '
                                        + 'tried = players it was tried for, created = offers it gave.'}>
                                        <InfoOutlinedIcon fontSize="small" color="action" />
                                    </Tooltip>
                                    <Box sx={{ flexGrow: 1 }} />
                                    <Tooltip title={data.templates.length === 0 ? 'Create a template first' : ''}>
                                        <span>
                                            <Button variant="contained" startIcon={<AddIcon />} disabled={data.templates.length === 0}
                                                onClick={() => setEditingRule('new')}>
                                                New rule
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Stack>
                                {data.rules.length === 0
                                    ? <Typography variant="body2" color="text.secondary">No rules.</Typography>
                                    : (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Name</TableCell>
                                                    <TableCell>Template</TableCell>
                                                    <TableCell>Enabled</TableCell>
                                                    <TableCell align="right">Priority</TableCell>
                                                    <TableCell>Tags</TableCell>
                                                    <TableCell>OS</TableCell>
                                                    <TableCell>Window</TableCell>
                                                    <TableCell align="right">Claim days</TableCell>
                                                    <TableCell>Push</TableCell>
                                                    <TableCell align="right">Tried / created</TableCell>
                                                    <TableCell align="right" />
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {data.rules.map(r => (
                                                    <TableRow key={r.id} sx={{ opacity: r.enabled ? 1 : 0.6 }}>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{r.id}</Typography>
                                                        </TableCell>
                                                        <TableCell>{r.offerName ?? `#${r.offerId}`}</TableCell>
                                                        <TableCell>
                                                            <Switch size="small" checked={r.enabled} disabled={busy} onChange={() => toggleRule(r)} />
                                                        </TableCell>
                                                        <TableCell align="right">{r.priority}</TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" gap={0.5} flexWrap="wrap">
                                                                {r.userTags.length === 0 && r.excludedTags.length === 0 && (
                                                                    <Typography variant="caption" color="text.secondary">everyone</Typography>
                                                                )}
                                                                {r.userTags.map(tag => <Chip key={tag} size="small" label={tag} />)}
                                                                {r.excludedTags.map(tag => (
                                                                    <Chip key={'x' + tag} size="small" color="error" variant="outlined" label={`not ${tag}`} />
                                                                ))}
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>{r.osTypes.length === 0 ? 'any' : r.osTypes.join(', ')}</TableCell>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                            <Typography variant="caption" component="div">{r.startAt ? formatDate(r.startAt) : 'from now'}</Typography>
                                                            <Typography variant="caption" component="div">{r.endAt ? `→ ${formatDate(r.endAt)}` : '→ no end'}</Typography>
                                                        </TableCell>
                                                        <TableCell align="right">{r.claimDays ?? <Typography variant="caption" color="text.secondary">template's</Typography>}</TableCell>
                                                        <TableCell>
                                                            {r.push
                                                                ? <Chip size="small" variant="outlined" icon={<NotificationsActiveOutlinedIcon />} label="push" />
                                                                : <Typography variant="caption" color="text.secondary">silent</Typography>}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                            {r.tried.toLocaleString()} / {r.created.toLocaleString()}
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                            <Tooltip title="Edit">
                                                                <IconButton size="small" onClick={() => setEditingRule(r)}><EditOutlinedIcon fontSize="small" /></IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete">
                                                                <IconButton size="small" color="error" onClick={() => setDeletingRule(r)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                            </CardContent>
                        </Card>
                    </>
                )}
            </Stack>

            {data && editingTemplate != null && (
                <TemplateDialog
                    template={editingTemplate === 'new' ? null : editingTemplate}
                    plans={plans}
                    options={options}
                    onClose={() => setEditingTemplate(null)}
                    onSave={async request => {
                        const t = editingTemplate;
                        const failed = await run(
                            () => t === 'new' ? createSubscriptionOfferTemplate(request) : updateSubscriptionOfferTemplate(t.id, request),
                            `${request.name} saved`);
                        if (failed == null) setEditingTemplate(null);
                        return failed;
                    }}
                />
            )}
            {data && editingRule != null && (
                <RuleDialog
                    rule={editingRule === 'new' ? null : editingRule}
                    templates={data.templates}
                    tags={tags}
                    onClose={() => setEditingRule(null)}
                    onSave={async request => {
                        const r = editingRule;
                        const failed = await run(
                            () => r === 'new' ? createSubscriptionOfferRule(request) : updateSubscriptionOfferRule(r.id, request),
                            `${request.name} saved`);
                        if (failed == null) setEditingRule(null);
                        return failed;
                    }}
                />
            )}
            <ConfirmDialog
                open={deletingRule != null}
                title={`Delete rule “${deletingRule?.name ?? ''}”?`}
                text="It stops giving its template. Offers it already gave stay as they are."
                confirmLabel="Delete"
                confirmColor="error"
                onClose={() => setDeletingRule(null)}
                onConfirm={async () => {
                    const r = deletingRule;
                    setDeletingRule(null);
                    if (r) setError(await run(() => deleteSubscriptionOfferRule(r.id), `${r.name} deleted`));
                }}
            />
            <Snackbar open={toast != null} autoHideDuration={3000} message={toast ?? ''} onClose={() => setToast(null)} />
        </Box>
    );
}

function ruleRequestOf(rule: SubscriptionOfferRuleDto): SubscriptionOfferRuleRequest {
    return {
        name: rule.name,
        offerId: rule.offerId,
        enabled: rule.enabled,
        priority: rule.priority,
        userTags: rule.userTags,
        excludedTags: rule.excludedTags,
        osTypes: rule.osTypes,
        startAt: rule.startAt == null ? null : new Date(rule.startAt).toISOString(),
        endAt: rule.endAt == null ? null : new Date(rule.endAt).toISOString(),
        claimDays: rule.claimDays,
        push: rule.push,
    };
}

/** Effect groups a boost sets as a percent (any value above the plan's): win booster, loss cashback, shop coin bonus. */
const PERCENT_GROUPS = [703, 704, 705];
const MAX_PERCENT = 100;

/** A boost row being edited: a catalogue effect (Ace, no ads), a percent of a [PERCENT_GROUPS] group, or daily betcoins. */
interface BoostDraft {
    type: 'EFFECT' | 'DAILY_COINS';
    /** The effect group; null until one is picked. */
    group: number | null;
    effectId: number | null;
    percent: string;
    dailyCoins: string;
}

function percentValid(value: string): boolean {
    const n = Number(value);
    return value.trim() !== '' && n > 0 && n <= MAX_PERCENT && Number.isInteger(n * 2);
}

/** The template editor: name, claim days, active, and a section per plan with its own benefits and the boosts. */
function TemplateDialog({ template, plans, options, onClose, onSave }: {
    template: SubscriptionOfferTemplateDto | null;
    plans: SubscriptionPlanAdminDto[];
    options: EffectOptionDto[];
    onClose: () => void;
    /** Resolves the API's refusal, null when saved. */
    onSave: (request: SubscriptionOfferTemplateRequest) => Promise<string | null>;
}) {
    const [name, setName] = React.useState(template?.name ?? '');
    const [claimDays, setClaimDays] = React.useState(String(template?.claimDays ?? 7));
    const [active, setActive] = React.useState(template?.active ?? true);
    const [drafts, setDrafts] = React.useState<Record<string, BoostDraft[]>>(() => {
        const byPlan: Record<string, BoostDraft[]> = {};
        plans.forEach(p => { byPlan[p.code] = []; });
        (template?.benefits ?? []).forEach(b => {
            const code = b.plan ?? '';
            const group = b.group ?? options.find(o => o.id === b.effectId)?.group ?? null;
            const coef = b.percent ?? options.find(o => o.id === b.effectId)?.coef ?? null;
            (byPlan[code] = byPlan[code] ?? []).push({
                type: b.type === 'DAILY_COINS' ? 'DAILY_COINS' : 'EFFECT',
                group,
                effectId: b.effectId,
                percent: group != null && PERCENT_GROUPS.includes(group) && coef != null ? String(coef) : '',
                dailyCoins: b.dailyCoins != null ? String(b.dailyCoins) : '',
            });
        });
        return byPlan;
    });
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const update = (plan: string, index: number, change: Partial<BoostDraft>) =>
        setDrafts(all => ({ ...all, [plan]: all[plan].map((d, i) => i === index ? { ...d, ...change } : d) }));
    const remove = (plan: string, index: number) =>
        setDrafts(all => ({ ...all, [plan]: all[plan].filter((_, i) => i !== index) }));
    const add = (plan: string, type: BoostDraft['type']) =>
        setDrafts(all => ({ ...all, [plan]: [...(all[plan] ?? []), { type, group: null, effectId: null, percent: '', dailyCoins: '' }] }));

    const days = Number(claimDays);
    const daysValid = Number.isInteger(days) && days >= 1 && days <= MAX_CLAIM_DAYS;
    const rowsComplete = Object.values(drafts).every(list => list.every(d => d.type === 'EFFECT'
        ? (d.group != null && PERCENT_GROUPS.includes(d.group) ? percentValid(d.percent) : d.effectId != null)
        : d.dailyCoins.trim() !== '' && Number.isInteger(Number(d.dailyCoins)) && Number(d.dailyCoins) > 0));
    const missingPlans = plans.filter(p => p.active && (drafts[p.code] ?? []).length === 0);
    const valid = name.trim() !== '' && daysValid && rowsComplete;

    const save = async () => {
        setSaving(true);
        const benefits = Object.entries(drafts).flatMap(([plan, list]) => list.map(d => d.type === 'DAILY_COINS'
            ? { plan, type: 'DAILY_COINS' as const, dailyCoins: Number(d.dailyCoins) }
            : d.group != null && PERCENT_GROUPS.includes(d.group)
                ? { plan, type: 'EFFECT' as const, group: d.group, percent: Number(d.percent) }
                : { plan, type: 'EFFECT' as const, effectId: d.effectId }));
        const failed = await onSave({ name: name.trim(), claimDays: days, active, benefits });
        setSaving(false);
        setError(failed);
    };

    return (
        <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                {template ? `Edit template #${template.id}` : 'New offer template'}
                <IconButton size="small" onClick={onClose} disabled={saving}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack gap={2}>
                    <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                        <TextField label="Name" value={name} onChange={e => setName(e.target.value)} size="small" sx={{ flex: '1 1 260px' }}
                            inputProps={{ maxLength: 120 }} error={name.trim() === ''} />
                        <TextField label="Claim days" type="number" value={claimDays} onChange={e => setClaimDays(e.target.value)} size="small"
                            sx={{ width: 180 }} inputProps={{ min: 1, max: MAX_CLAIM_DAYS }} error={!daysValid}
                            helperText={`Days to take it, 1–${MAX_CLAIM_DAYS}`} />
                        <FormControlLabel control={<Switch checked={active} onChange={e => setActive(e.target.checked)} />}
                            label={active ? 'Active (can be given)' : 'Inactive'} />
                    </Stack>
                    {plans.map(plan => (
                        <PlanBoostsSection key={plan.id} plan={plan} options={options} drafts={drafts[plan.code] ?? []}
                            onChange={(i, change) => update(plan.code, i, change)}
                            onRemove={i => remove(plan.code, i)}
                            onAdd={type => add(plan.code, type)} />
                    ))}
                    {missingPlans.length > 0 && (
                        <Alert severity="warning">
                            Every active plan needs at least one boost: {missingPlans.map(p => p.name).join(', ')}.
                        </Alert>
                    )}
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" disabled={!valid || saving} onClick={save}>
                    {saving ? <CircularProgress size={18} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/** One plan in the template editor: what the plan gives (for reference) and the offer's boosts for it. */
function PlanBoostsSection({ plan, options, drafts, onChange, onRemove, onAdd }: {
    plan: SubscriptionPlanAdminDto;
    options: EffectOptionDto[];
    drafts: BoostDraft[];
    onChange: (index: number, change: Partial<BoostDraft>) => void;
    onRemove: (index: number) => void;
    onAdd: (type: BoostDraft['type']) => void;
}) {
    const ownCoins = planDailyCoins(plan);
    const groups = Object.keys(GROUP_INFO).map(Number);
    /** Catalogue effects of [group] that beat the plan's own (Ace access, no ads). */
    const stronger = (group: number) => options.filter(o => {
        if (o.group !== group) return false;
        const own = planEffectOf(plan, group, options);
        return own == null || o.coef > own.coef;
    });
    /** Groups row [rowIndex] can boost: not used by another row, a percent group, or one with a stronger catalogue effect. */
    const freeGroups = (rowIndex: number) => {
        const taken = new Set(drafts.filter((d, i) => i !== rowIndex && d.type === 'EFFECT').map(d => d.group));
        return groups.filter(g => !taken.has(g) && (PERCENT_GROUPS.includes(g) || stronger(g).length > 0));
    };
    const hasDaily = drafts.some(d => d.type === 'DAILY_COINS');

    return (
        <Card variant="outlined" sx={{ opacity: plan.active ? 1 : 0.7 }}>
            <CardContent>
                <Stack gap={1.25}>
                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                        <WorkspacePremiumIcon color={plan.code === 'max' ? 'warning' : 'primary'} />
                        <Typography variant="subtitle1" fontWeight={700}>{plan.name}</Typography>
                        <Chip size="small" label={plan.code} sx={{ fontFamily: 'monospace' }} />
                        <SubscriptionTierChip tier={plan.tier} />
                        {!plan.active && <Chip size="small" label="inactive · boosts optional" />}
                    </Stack>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Plan's own</Typography>
                        <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                            {plan.benefits.length === 0 && <Typography variant="caption" color="text.secondary">nothing</Typography>}
                            {plan.benefits.map((b, i) => {
                                const view = benefitView(b, options);
                                return (
                                    <Tooltip key={i} title={view.detail}>
                                        <Chip size="small" variant="outlined" icon={view.icon} label={view.label} />
                                    </Tooltip>
                                );
                            })}
                        </Stack>
                    </Box>
                    <Divider />
                    <Typography variant="caption" color="text.secondary">Offer boosts</Typography>
                    {drafts.length === 0 && (
                        <Typography variant="body2" color={plan.active ? 'warning.main' : 'text.secondary'}>No boosts.</Typography>
                    )}
                    {drafts.map((d, i) => {
                        const coins = Number(d.dailyCoins);
                        const coinsWeak = d.dailyCoins.trim() !== '' && coins <= ownCoins;
                        const own = d.group != null ? planEffectOf(plan, d.group, options) : null;
                        const isPercent = d.group != null && PERCENT_GROUPS.includes(d.group);
                        const percentWeak = isPercent && d.percent.trim() !== '' && own != null && Number(d.percent) <= own.coef;
                        const percentBad = isPercent && d.percent.trim() !== '' && !percentValid(d.percent);
                        return (
                            <Stack key={i} direction="row" gap={1} alignItems="flex-start">
                                <Box sx={{ color: 'primary.main', display: 'flex', width: 24, pt: 1 }}>
                                    {d.type === 'DAILY_COINS' ? <RedeemIcon fontSize="small" /> : groupIcon(d.group ?? undefined)}
                                </Box>
                                {d.type === 'EFFECT' && (
                                    <TextField select size="small" label="Benefit" value={d.group ?? ''} sx={{ width: 190 }}
                                        onChange={e => onChange(i, { group: Number(e.target.value), effectId: null, percent: '' })}>
                                        {Array.from(new Set([...(d.group != null ? [d.group] : []), ...freeGroups(i)])).map(g => (
                                            <MenuItem key={g} value={g}>{GROUP_INFO[g]?.label ?? `Group ${g}`}</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                                {d.type === 'EFFECT' && isPercent && (
                                    <TextField size="small" type="number" label="Percent" value={d.percent} sx={{ flex: 1 }}
                                        inputProps={{ min: 0.5, max: MAX_PERCENT, step: 0.5 }}
                                        InputProps={{ endAdornment: <Typography variant="body2" color="text.secondary">%</Typography> }}
                                        onChange={e => onChange(i, { percent: e.target.value })}
                                        error={percentWeak || percentBad}
                                        helperText={percentBad ? `Above 0, at most ${MAX_PERCENT}, in steps of 0.5`
                                            : percentWeak ? `Must beat the plan's ${own?.coef}%`
                                            : own ? `The plan gives ${own.coef}%` : `The plan has no ${GROUP_INFO[d.group!]?.label ?? 'effect of this group'}`} />
                                )}
                                {d.type === 'EFFECT' && d.group != null && !isPercent && (
                                    <TextField select size="small" value={d.effectId ?? ''} sx={{ flex: 1 }} label="Stronger effect"
                                        onChange={e => onChange(i, { effectId: Number(e.target.value) })}
                                        helperText={own ? `Plan's: ${own.name ?? `#${own.id}`} (coef ${own.coef})` : ' '}>
                                        {d.effectId != null && !stronger(d.group).some(o => o.id === d.effectId) && (
                                            <MenuItem value={d.effectId}>
                                                {options.find(o => o.id === d.effectId)?.name ?? `Effect ${d.effectId}`} — not stronger
                                            </MenuItem>
                                        )}
                                        {stronger(d.group).map(o => (
                                            <MenuItem key={o.id} value={o.id}>{o.name ?? `Effect ${o.id}`} (coef {o.coef})</MenuItem>
                                        ))}
                                    </TextField>
                                )}
                                {d.type === 'EFFECT' && d.group == null && (
                                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1, pt: 1 }}>Pick a benefit</Typography>
                                )}
                                {d.type === 'DAILY_COINS' && (
                                    <TextField size="small" type="number" label="Betcoins a day" value={d.dailyCoins} sx={{ flex: 1 }}
                                        inputProps={{ min: ownCoins + 1 }} onChange={e => onChange(i, { dailyCoins: e.target.value })}
                                        error={coinsWeak}
                                        helperText={coinsWeak ? `Must exceed the plan's ${ownCoins.toLocaleString()}` : `The plan gives ${ownCoins.toLocaleString()} a day`} />
                                )}
                                <IconButton size="small" color="error" sx={{ mt: 0.5 }} onClick={() => onRemove(i)}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        );
                    })}
                    <Stack direction="row" gap={1}>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => onAdd('EFFECT')}
                            disabled={freeGroups(-1).length === 0}>
                            Benefit
                        </Button>
                        <Button size="small" startIcon={<AddIcon />} onClick={() => onAdd('DAILY_COINS')} disabled={hasDaily}>
                            Daily betcoins
                        </Button>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

/** The rule editor: template, tags, OS, window, claim days, push. */
function RuleDialog({ rule, templates, tags, onClose, onSave }: {
    rule: SubscriptionOfferRuleDto | null;
    templates: SubscriptionOfferTemplateDto[];
    tags: UserTagDefinitionDto[];
    onClose: () => void;
    /** Resolves the API's refusal, null when saved. */
    onSave: (request: SubscriptionOfferRuleRequest) => Promise<string | null>;
}) {
    const [name, setName] = React.useState(rule?.name ?? '');
    const [offerId, setOfferId] = React.useState<number | ''>(rule?.offerId ?? templates.find(t => t.active)?.id ?? '');
    const [enabled, setEnabled] = React.useState(rule?.enabled ?? false);
    const [priority, setPriority] = React.useState(String(rule?.priority ?? 0));
    const [userTags, setUserTags] = React.useState<string[]>(rule?.userTags ?? []);
    const [excludedTags, setExcludedTags] = React.useState<string[]>(rule?.excludedTags ?? []);
    const [osTypes, setOsTypes] = React.useState<string[]>(rule?.osTypes ?? []);
    const [startAt, setStartAt] = React.useState(toLocalInput(rule?.startAt ?? null));
    const [endAt, setEndAt] = React.useState(toLocalInput(rule?.endAt ?? null));
    const [claimDays, setClaimDays] = React.useState(rule?.claimDays != null ? String(rule.claimDays) : '');
    const [push, setPush] = React.useState(rule?.push ?? true);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const tagNames = tags.map(t => t.tagName);
    const template = templates.find(t => t.id === offerId) ?? null;
    const priorityValue = Number(priority);
    const days = Number(claimDays);
    const daysValid = claimDays.trim() === '' || (Number.isInteger(days) && days >= 1 && days <= MAX_CLAIM_DAYS);
    const windowValid = startAt === '' || endAt === '' || new Date(endAt) > new Date(startAt);
    const valid = name.trim() !== '' && offerId !== '' && priority.trim() !== '' && Number.isInteger(priorityValue) && daysValid && windowValid;

    const save = async () => {
        if (offerId === '') return;
        setSaving(true);
        const failed = await onSave({
            name: name.trim(),
            offerId,
            enabled,
            priority: priorityValue,
            userTags,
            excludedTags,
            osTypes,
            startAt: toIsoOrNull(startAt),
            endAt: toIsoOrNull(endAt),
            claimDays: claimDays.trim() === '' ? null : days,
            push,
        });
        setSaving(false);
        setError(failed);
    };

    return (
        <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                {rule ? `Edit rule ${rule.name}` : 'New offer rule'}
                <IconButton size="small" onClick={onClose} disabled={saving}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack gap={2}>
                    <TextField label="Name" value={name} onChange={e => setName(e.target.value)} size="small" inputProps={{ maxLength: 120 }} />
                    <TextField select label="Template" value={offerId} size="small" onChange={e => setOfferId(Number(e.target.value))}
                        helperText={template && !template.active ? 'This template is inactive: the rule gives nothing until it is active again' : ' '}
                        error={template != null && !template.active}>
                        {templates.map(t => (
                            <MenuItem key={t.id} value={t.id}>{t.name}{t.active ? '' : ' (inactive)'} · {t.claimDays} days</MenuItem>
                        ))}
                    </TextField>
                    <Stack direction="row" gap={2} alignItems="center">
                        <FormControlLabel control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />} label="Enabled" />
                        <TextField label="Priority" type="number" value={priority} onChange={e => setPriority(e.target.value)} size="small"
                            sx={{ width: 140 }} helperText="Higher wins" />
                        <TextField label="Claim days" type="number" value={claimDays} onChange={e => setClaimDays(e.target.value)} size="small"
                            sx={{ width: 160 }} error={!daysValid}
                            helperText={`Empty = the template's${template ? ` (${template.claimDays})` : ''}`}
                            inputProps={{ min: 1, max: MAX_CLAIM_DAYS }} />
                    </Stack>
                    <Autocomplete
                        multiple
                        options={tagNames}
                        value={userTags}
                        onChange={(_, value) => setUserTags(value)}
                        renderInput={params => <TextField {...params} label="User tags (any of them; empty = everyone)" />}
                    />
                    <Autocomplete
                        multiple
                        options={tagNames}
                        value={excludedTags}
                        onChange={(_, value) => setExcludedTags(value)}
                        renderInput={params => <TextField {...params} label="Excluded tags" />}
                    />
                    <Autocomplete
                        multiple
                        options={['android', 'ios']}
                        value={osTypes}
                        onChange={(_, value) => setOsTypes(value)}
                        renderInput={params => <TextField {...params} label="OS types (empty = any)" />}
                    />
                    <Stack direction="row" gap={2}>
                        <TextField label="Start" type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} size="small"
                            InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} helperText="Empty = now" />
                        <TextField label="End" type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} size="small"
                            InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} error={!windowValid}
                            helperText={windowValid ? 'Empty = no end' : 'Must be after the start'} />
                    </Stack>
                    <FormControlLabel control={<Switch checked={push} onChange={e => setPush(e.target.checked)} />}
                        label="Send a push when it gives the offer" />
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="contained" disabled={!valid || saving} onClick={save}>
                    {saving ? <CircularProgress size={18} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
